import React, { useState, useEffect, useRef, useCallback } from "react";
import { Navigate, Outlet, Link, useLocation } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  Shield,
  Building2,
  Settings,
  Bot,
  KeyRound,
  Fingerprint,
  Sliders,
  ScanFace,
  UserSearch,
  Moon,
  Sun,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  LogOut,
  Users,
  Activity,
  ShieldAlert,
} from "lucide-react";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";
import { cn } from "lib/utils";
import { withV } from "lib/asset";
import FaceEnrollButton from "components/FaceEnrollButton";
import SearchOverlay from "components/SearchOverlay";

// `requires` = capability key returned by /api/auth/me/ (user.capabilities).
// Items/sections without `requires` are visible to every authenticated user.
const menuStructure = [
  {
    id: "sessions",
    path: "/liveness/sessions",
    icon: ScanFace,
    labelKey: "nav.verifications",
    requires: "review_recordings",
    children: null,
  },
  {
    id: "client-apps",
    path: "/liveness/admin/client-apps",
    icon: KeyRound,
    labelKey: "nav.clientApps",
    requires: "manage_settings",
    children: null,
  },
  {
    id: "api-tokens",
    path: "/liveness/admin/api-tokens",
    icon: Fingerprint,
    labelKey: "nav.apiTokens",
    requires: "manage_settings",
    children: null,
  },
  {
    // The group is visible to institution admins (manage_users) so Accounts stays
    // reachable; the system-wide children gate individually on manage_system.
    id: "admin",
    icon: Shield,
    labelKey: "nav.admin",
    requires: "manage_users",
    children: [
      { id: "accounts", path: "/liveness/admin/accounts", labelKey: "nav.accounts", icon: Users, requires: "manage_users" },
      { id: "people", path: "/liveness/admin/people", labelKey: "nav.people", icon: UserSearch, requires: "manage_system" },
      { id: "biometrics", path: "/liveness/admin/biometrics", labelKey: "nav.biometrics", icon: Fingerprint, requires: "manage_system" },
      { id: "alerts", path: "/liveness/admin/alerts", labelKey: "nav.alerts", icon: ShieldAlert, requires: "manage_system" },
      { id: "companies", path: "/liveness/admin/companies", labelKey: "nav.companies", icon: Building2, requires: "manage_system" },
      { id: "ai", path: "/liveness/admin/ai", labelKey: "nav.ai", icon: Bot, requires: "manage_system" },
      { id: "jobs", path: "/liveness/admin/jobs", labelKey: "nav.jobs", icon: Activity, requires: "manage_system" },
      { id: "global", path: "/liveness/admin/global", labelKey: "nav.global", icon: Sliders, requires: "manage_system" },
      { id: "settings", path: "/liveness/admin/settings", labelKey: "nav.settings", icon: Settings, requires: "manage_system" },
    ],
  },
];

// Returns true if the current user is allowed to see a menu item/section.
const canSee = (item, user) => {
  if (!item.requires) return true;
  return Boolean(user?.capabilities?.[item.requires]);
};

const findActiveMenu = (pathname) => {
  for (const menu of menuStructure) {
    if (menu.path === pathname) {
      return { menuId: menu.id, subMenuId: null };
    }
    // Keep a top-level item active on its sub-routes (e.g. /liveness/review/:id
    // highlights "Console"). Excludes "/" so the root never matches everything.
    if (menu.path && menu.path !== "/" && pathname.startsWith(menu.path + "/")) {
      return { menuId: menu.id, subMenuId: null };
    }
    if (menu.children) {
      const sortedChildren = [...menu.children].sort(
        (a, b) => b.path.length - a.path.length
      );
      for (const child of sortedChildren) {
        if (pathname === child.path || pathname.startsWith(child.path + "/")) {
          return { menuId: menu.id, subMenuId: child.id };
        }
      }
    }
  }
  return { menuId: "client-apps", subMenuId: null };
};

export default function AppLayout({ darkMode, setDarkMode }) {
  const { user, loading, logout } = useAuth();
  const { t } = useI18n();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved === "true";
  });
  const [expandedMenus, setExpandedMenus] = useState({});
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [flyoutPosition, setFlyoutPosition] = useState({ top: 0 });
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const hoverTimeoutRef = useRef(null);
  const location = useLocation();

  const { menuId: activeMenuId, subMenuId: activeSubMenuId } =
    findActiveMenu(location.pathname);

  useEffect(() => {
    if (!sidebarCollapsed && activeMenuId) {
      setExpandedMenus({ [activeMenuId]: true });
    }
  }, [activeMenuId, sidebarCollapsed]);

  useEffect(() => {
    const checkScreenSize = () => setIsSmallScreen(window.innerWidth < 1024);
    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", sidebarCollapsed);
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (!sidebarCollapsed) setHoveredMenu(null);
  }, [sidebarCollapsed]);

  const effectiveCollapsed = isSmallScreen || sidebarCollapsed;

  const toggleMenu = (menuId) => {
    if (!effectiveCollapsed) {
      setExpandedMenus((prev) => ({ ...prev, [menuId]: !prev[menuId] }));
    }
  };

  const handleMenuHover = (menuId, event) => {
    if (effectiveCollapsed) {
      clearTimeout(hoverTimeoutRef.current);
      setHoveredMenu(menuId);
      if (event && event.currentTarget) {
        const rect = event.currentTarget.getBoundingClientRect();
        setFlyoutPosition({ top: rect.top });
      }
    }
  };

  const handleMenuLeave = () => {
    if (effectiveCollapsed) {
      hoverTimeoutRef.current = setTimeout(() => setHoveredMenu(null), 150);
    }
  };

  const renderMenuItem = (item) => {
    if (!canSee(item, user)) return null;

    const Icon = item.icon;
    const visibleChildren = (item.children || []).filter((c) => canSee(c, user));
    const hasChildren = visibleChildren.length > 0;
    const isExpanded = expandedMenus[item.id];
    const isActive = item.id === activeMenuId;
    const isHovered = hoveredMenu === item.id;

    if (!hasChildren) {
      return (
        <div key={item.id}>
          <Link
            to={item.path}
            className={cn(
              "flex items-center transition-all text-sm relative",
              effectiveCollapsed
                ? "justify-center p-2.5"
                : "justify-start gap-3 px-4 py-2.5",
              isActive
                ? "text-emerald-400 font-medium"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/30"
            )}
          >
            <Icon size={18} />
            {!effectiveCollapsed && (
              <span className="font-medium text-sm">{t(item.labelKey)}</span>
            )}
          </Link>
        </div>
      );
    }

    return (
      <div
        key={item.id}
        className="relative"
        onMouseEnter={(e) => handleMenuHover(item.id, e)}
        onMouseLeave={handleMenuLeave}
      >
        <button
          onClick={() => toggleMenu(item.id)}
          className={cn(
            "w-full flex items-center transition-all text-sm",
            effectiveCollapsed
              ? "justify-center p-2.5"
              : "justify-between px-4 py-2.5",
            isActive
              ? "text-foreground"
              : "text-muted-foreground hover:text-foreground hover:bg-muted/30"
          )}
        >
          <div className="flex items-center gap-3">
            <Icon size={18} />
            {!effectiveCollapsed && (
              <span className="font-medium text-sm">{t(item.labelKey)}</span>
            )}
          </div>
          {!effectiveCollapsed && (
            <ChevronDown
              size={16}
              className={cn(
                "transition-transform duration-200",
                isExpanded ? "rotate-180" : ""
              )}
            />
          )}
        </button>

        {effectiveCollapsed &&
          isHovered &&
          createPortal(
            <div
              className="fixed min-w-[180px]"
              style={{
                left: "72px",
                top: `${flyoutPosition.top}px`,
                zIndex: 99999,
              }}
              onMouseEnter={(e) => handleMenuHover(item.id, e)}
              onMouseLeave={handleMenuLeave}
            >
              <div className="absolute left-0 top-3 -ml-1.5 w-3 h-3 bg-popover border-l border-t border-border rotate-[-45deg]" style={{ zIndex: 99999 }} />
              <div className="bg-popover border border-border rounded-lg shadow-2xl py-2 ml-1">
                <div className="px-3 py-2 border-b border-border">
                  <span className="font-semibold text-sm">{t(item.labelKey)}</span>
                </div>
                {visibleChildren.map((child) => {
                  const isChildActive = child.id === activeSubMenuId;
                  const ChildIcon = child.icon;
                  return (
                    <Link
                      key={child.id}
                      to={child.path}
                      className={cn(
                        "flex items-center gap-2.5 px-4 py-2 text-sm transition-colors",
                        isChildActive
                          ? "text-emerald-400 bg-emerald-400/10 font-medium"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      )}
                      onClick={() => setHoveredMenu(null)}
                    >
                      {ChildIcon && <ChildIcon size={16} className="flex-shrink-0" />}
                      <span>{t(child.labelKey)}</span>
                    </Link>
                  );
                })}
              </div>
            </div>,
            document.body
          )}

        {!effectiveCollapsed && isExpanded && (
          <div className="space-y-0.5 bg-black/20">
            {visibleChildren.map((child) => {
              const isChildActive = child.id === activeSubMenuId;
              const ChildIcon = child.icon;
              return (
                <Link
                  key={child.id}
                  to={child.path}
                  className={cn(
                    "flex items-center gap-3 w-full pl-8 pr-4 py-2 text-sm transition-colors",
                    isChildActive
                      ? "text-emerald-400 font-medium"
                      : "text-muted-foreground/70 hover:text-foreground hover:bg-white/5"
                  )}
                >
                  {ChildIcon && <ChildIcon size={16} className="flex-shrink-0" />}
                  <span>{t(child.labelKey)}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/liveness/login" replace />;
  }

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-background">
      {/* Top Header */}
      <header className="flex items-center justify-between bg-card border-b border-border z-50">
        <div className="flex items-center justify-center px-4 py-3 border-r border-border w-52">
          <img
            src={withV(darkMode
              ? "https://media.sec4us.com.br/logo/sec4us-dark-mode.svg"
              : "https://media.sec4us.com.br/logo/sec4us-light-mode.svg")}
            alt="Sec4US"
            className="h-7 w-auto"
          />
        </div>

        <div className="flex items-center gap-4 flex-1 justify-end px-6 py-3">
          {/* User info */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Link to="/liveness/profile" title={t("profile.title", "Meus dados")}
              className="font-medium text-foreground hover:text-primary transition-colors inline-flex items-center gap-1.5">
              {user?.first_name || user?.email}
              {(user && (user.email_verified === false || user.phone_verified === false)) && (
                <span title={t("profile.unverified", "Não verificado")}
                  className="h-1.5 w-1.5 rounded-full bg-amber-500" />
              )}
            </Link>
            {user?.role_display && (
              <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-400/10 text-emerald-400 border border-emerald-400/20">
                {user.role_display}
              </span>
            )}
          </div>

          <div className="h-6 w-px bg-border" />

          {/* Self-service facial biometrics enrollment */}
          <FaceEnrollButton />

          <div className="h-6 w-px bg-border" />

          {/* Dark Mode Toggle */}
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="p-2 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            {darkMode ? <Sun size={20} /> : <Moon size={20} />}
          </button>

          <div className="h-6 w-px bg-border" />

          {/* Logout */}
          <button
            onClick={logout}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <LogOut size={16} />
            <span>{t("nav.logout")}</span>
          </button>
        </div>
      </header>

      {/* Main container - Sidebar + Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <aside
          className={cn(
            "relative bg-card border-r border-border transition-all duration-300 ease-in-out flex-shrink-0",
            effectiveCollapsed ? "w-16" : "w-52"
          )}
        >
          <div className="flex flex-col h-full">
            <nav
              className={cn(
                "flex-1 space-y-0.5 overflow-y-auto scrollbar-thin pt-2",
                effectiveCollapsed ? "px-2" : "px-0"
              )}
            >
              {menuStructure.map(renderMenuItem)}
            </nav>

            {/* Footer with collapse toggle */}
            <div
              className={cn(
                "border-t border-border transition-all duration-300 flex items-end relative",
                isSmallScreen ? "hidden" : "flex",
                effectiveCollapsed
                  ? "p-2 justify-center min-h-[60px]"
                  : "p-3 justify-between min-h-[70px]"
              )}
            >
              {!effectiveCollapsed && (
                <div className="text-xs text-muted-foreground">
                  <div className="text-[11px] font-medium">SecFace v1.0.0</div>
                  <div className="text-[10px] text-muted-foreground/60">
                    &copy; Sec4US 2026
                  </div>
                </div>
              )}

              <button
                onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                className={cn(
                  "flex items-center justify-center transition-all duration-200",
                  effectiveCollapsed
                    ? "w-10 h-10 rounded-full bg-primary/10 hover:bg-primary/20 text-primary border border-primary/30 hover:border-primary/50 shadow-sm"
                    : "absolute right-0 bottom-[10px] w-[35px] h-[45px] rounded-l-lg bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground"
                )}
              >
                {effectiveCollapsed ? (
                  <ChevronRight size={18} />
                ) : (
                  <ChevronLeft size={20} />
                )}
              </button>
            </div>
          </div>
        </aside>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6 bg-background relative">
          <Outlet />
        </main>
        {/* Ctrl/Cmd+F anywhere in the authenticated app */}
        <SearchOverlay />
      </div>
    </div>
  );
}
